import React, { CSSProperties, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { getOtherAddresses } from "../../../actions/makeAWish";
import moment from "moment";
import { states } from "../../../helpers/makeAWishConstants";
import LoadingTable from "../../material-components/WidgetSkeleton/LoadingTable";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableContainer from "@material-ui/core/TableContainer";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import TextField from "@material-ui/core/TextField";
import FormControl from "@material-ui/core/FormControl";
import InputLabel from "@material-ui/core/InputLabel";
import Select from "@material-ui/core/Select";
import MenuItem from "@material-ui/core/MenuItem";
import { IAppState } from "../../../types/stores/index";
import { Address } from "../../../types/stores/makeAWish";
import { IconButton, Paper, Tooltip } from "@material-ui/core";

type Props = {
    id: string;
    type: OtherType;
    onChange: (address: Address | undefined, saveAllowed: boolean, type: OtherType) => void;
};

type OtherType = "" | "add" | "edit" | "delete";

const OtherAddresses: React.FC<Props> = ({ id, type, onChange }: Props) => {
    const dispatch = useDispatch();

    const tableCell: CSSProperties = {
        fontSize: "12px",
        alignSelf: "center",
        fontFamily: "Roboto",
        padding: "5px",
        maxWidth: "80px",
        whiteSpace: "nowrap",
        overflow: "hidden!important",
        textOverflow: "ellipsis",
    };

    const otherAddresses = useSelector((state: IAppState) => state.makeAWish.otherAddresses);
    const donor = useSelector((state: IAppState) => state.makeAWish.editDonorsData.find(x => x.Id == id)!);
    const addressTypes = useSelector((state: IAppState) => state.makeAWish.addressTypes);
    const isLoading = useSelector((state: IAppState) => state.makeAWish.isLoading);

    const newAddress: Address = {
        IdentNumber: id,
        SequenceNumber: "",
        AddressType: "A",
        City: "",
        State: "",
        Zip: "",
        Zip4: "",
        Salutation: (donor && donor.Salutation) || "",
        HouseNumber: "",
        Street: "",
        Address2: "",
        Address3: "",
        Telephone: "",
        Fax: "",
        InternetAddress: "",
        SeasonalBeginDate: "",
        SeasonalEndDate: "",
        County: "",
        Country: "",
        RowId: "",
        Notes: "",
    };

    const [isEdit, setEdit] = useState(false);
    const [isAdd, setAdd] = useState(false);
    const [isDelete, setDelete] = useState(false);
    const [selected, setSelected] = useState<Address>(newAddress);
    const [isEmailsValid, setIsEmailsValid] = useState(true);

    useEffect(() => {
        if (type == "") {
            dispatch(getOtherAddresses(id));
            setEdit(false);
            setAdd(false);
            setDelete(false);
        }
    }, [id, type, dispatch]);

    const setEditValues = (event, attribute) => {
        let data: Address = { ...selected };

        data.IdentNumber = selected.IdentNumber;
        data.SequenceNumber = selected.SequenceNumber;
        data.AddressType = attribute == "AddressType" ? event.target.value : selected.AddressType;
        data.City = attribute == "City" ? event.target.value : selected.City;
        data.State = attribute == "State" ? event.target.value : selected.State;
        data.Zip = attribute == "Zip" ? event.target.value : selected.Zip;
        data.Zip4 = attribute == "Zip4" ? event.target.value : selected.Zip4;
        data.Salutation = attribute == "Salutation" ? event.target.Salutation : selected.Salutation;
        data.HouseNumber = attribute == "HouseNumber" ? event.target.value : selected.HouseNumber;
        data.Street = attribute == "Street" ? event.target.value : selected.Street;
        data.Address2 = attribute == "Address2" ? event.target.value : selected.Address2;
        data.Address3 = attribute == "Address3" ? event.target.value : selected.Address3;
        data.Telephone = attribute == "Telephone" ? event.target.value : selected.Telephone;
        data.Fax = attribute == "Fax" ? event.target.value : selected.Fax;
        data.InternetAddress = attribute == "InternetAddress" ? event.target.value : selected.InternetAddress;
        data.SeasonalBeginDate = attribute == "SeasonalBeginDate" ? event.target.value : selected.SeasonalBeginDate;
        data.SeasonalEndDate = attribute == "SeasonalEndDate" ? event.target.value : selected.SeasonalEndDate;
        data.County = attribute == "County" ? event.target.value : selected.County;
        data.Country = attribute == "Country" ? event.target.value : selected.Country;
        data.RowId = selected.RowId;
        data.Notes = attribute == "Notes" ? event.target.value : selected.Notes;

        setSelected(data);
        const validateResult = validate(data);
        if (validateResult) {
            onChange(data, true, type);
        } else {
            onChange(data, false, type);
        }
    };

    const formatDate = (date: string) => moment(date, "MM/DD/YYYY").format("YYYY-MM-DD").toString();

    function validateEmails(string) {
        if (string == undefined || !string.trim()) {
            setIsEmailsValid(true);
            return true;
        }

        const regex = /^([\w-\.]+@([\w-]+\.)+[\w-]{2,4})?$/;
        const result = string.replace(/\s/g, "").split(/,|;/);
        for (let i = 0; i < result.length; i++) {
            if (!regex.test(result[i])) {
                setIsEmailsValid(false);
                return false;
            }
        }

        setIsEmailsValid(true);
        return true;
    }

    const validate = (data: Address) =>
        validateEmails(data.InternetAddress) &&
        !(
            (data.Zip && data.Zip.length != 5) ||
            (data.Zip4 && data.Zip4.length != 4) ||
            (data.Telephone && data.Telephone.length != 10) ||
            (data.Fax && data.Fax.length != 10)
        );

    const handleEmails = e => {
        const emailResult = validateEmails(e.target.value);
        const updateSelected: Address = { ...selected };
        updateSelected.InternetAddress = e.target.value as string;
        setSelected(updateSelected);
        if (emailResult || e.target.value == "") {
            const validateResult = validate(updateSelected);
            if (validateResult) {
                onUpdate(updateSelected);
            }
        } else {
            onChange(updateSelected, false, type);
        }
    };

    const onEditClick = (rowId: string) => {
        const address = otherAddresses.find(x => x.RowId == rowId)!;
        const validateResult = validate(address);
        setSelected(address);
        setEdit(true);
        if (validateResult) {
            onChange(address, true, "edit");
        } else {
            onChange(address, false, "edit");
        }
    };

    const onAddClick = () => {
        setSelected(newAddress);
        setAdd(true);
        onChange(newAddress, true, "add");
    };

    const onDeleteClick = (rowId: string) => {
        const address = otherAddresses.find(x => x.RowId == rowId)!;
        setSelected(address);
        setDelete(true);
        onChange(address, true, "delete");
    };

    const onUpdate = (address: Address) => onChange(address, validate(address), type);

    const onBackClick = () => {
        setSelected(newAddress);
        setEdit(false);
        setAdd(false);
        setDelete(false);
        onChange(undefined, false, "");
    };

    const renderResultTable = () => {
        if (otherAddresses.length == 0) {
            return (
                <div>
                    <p>No Other Addresses Present</p>
                </div>
            );
        }

        return (
            <Paper>
                <TableContainer style={{ maxHeight: "500px" }}>
                    <Table stickyHeader>
                        <TableHead style={{ marginTop: "0px" }}>
                            <TableRow>
                                <TableCell></TableCell>
                                <TableCell style={{ ...tableCell }}>Address Type</TableCell>
                                <TableCell style={{ ...tableCell }}>City</TableCell>
                                <TableCell style={{ ...tableCell }}>State</TableCell>
                                <TableCell style={{ ...tableCell }}>Zip</TableCell>
                                <TableCell style={{ ...tableCell }}>Zip4</TableCell>
                                <TableCell style={{ ...tableCell }}>Salutation</TableCell>
                                <TableCell style={{ ...tableCell }}>House Number</TableCell>
                                <TableCell style={{ ...tableCell }}>Street</TableCell>
                                <TableCell style={{ ...tableCell }}>Address2</TableCell>
                                <TableCell style={{ ...tableCell }}>Address3</TableCell>
                                <TableCell style={{ ...tableCell }}>Telephone</TableCell>
                                <TableCell style={{ ...tableCell }}>Fax</TableCell>
                                <TableCell style={{ ...tableCell }}>Email</TableCell>
                                <TableCell style={{ ...tableCell }}>Begin</TableCell>
                                <TableCell style={{ ...tableCell }}>End</TableCell>
                                <TableCell style={{ ...tableCell }}>County</TableCell>
                                <TableCell style={{ ...tableCell }}>Country</TableCell>
                                <TableCell style={{ ...tableCell }}>Notes</TableCell>
                                <TableCell></TableCell>
                            </TableRow>
                        </TableHead>
                        {isLoading ? (
                            <LoadingTable cols={20} rows={5} />
                        ) : (
                            <TableBody>
                                {otherAddresses.map((row: Address, index: number) => (
                                    <TableRow key={index}>
                                        <TableCell style={{ ...tableCell }}>
                                            <Tooltip key={row.RowId + "-edit-button"} title="Edit">
                                                <IconButton onClick={() => onEditClick(row.RowId)} size="small">
                                                    <i className="material-icons" style={{ fontSize: 14 }}>
                                                        edit
                                                    </i>
                                                </IconButton>
                                            </Tooltip>
                                        </TableCell>
                                        <TableCell style={{ ...tableCell }}>{row.AddressType}</TableCell>
                                        <TableCell style={{ ...tableCell }}>{row.City}</TableCell>
                                        <TableCell style={{ ...tableCell }}>{row.State}</TableCell>
                                        <TableCell style={{ ...tableCell }}>{row.Zip}</TableCell>
                                        <TableCell style={{ ...tableCell }}>{row.Zip4}</TableCell>
                                        <TableCell style={{ ...tableCell }}>{row.Salutation}</TableCell>
                                        <TableCell style={{ ...tableCell }}>{row.HouseNumber}</TableCell>
                                        <TableCell style={{ ...tableCell }}>{row.Street}</TableCell>
                                        <TableCell style={{ ...tableCell }}>{row.Address2}</TableCell>
                                        <TableCell style={{ ...tableCell }}>{row.Address3}</TableCell>
                                        <TableCell style={{ ...tableCell }}>{row.Telephone}</TableCell>
                                        <TableCell style={{ ...tableCell }}>{row.Fax}</TableCell>
                                        <TableCell style={{ ...tableCell }}>{row.InternetAddress}</TableCell>
                                        <TableCell style={{ ...tableCell }}>{row.SeasonalBeginDate}</TableCell>
                                        <TableCell style={{ ...tableCell }}>{row.SeasonalEndDate}</TableCell>
                                        <TableCell style={{ ...tableCell }}>{row.County}</TableCell>
                                        <TableCell style={{ ...tableCell }}>{row.Country}</TableCell>
                                        <TableCell style={{ ...tableCell }}>{row.Notes}</TableCell>
                                        <TableCell style={{ ...tableCell }}>
                                            <Tooltip key={row.RowId + "-delete-button"} title="Delete">
                                                <IconButton onClick={() => onDeleteClick(row.RowId)} size="small">
                                                    <i className="material-icons" style={{ fontSize: 14 }}>
                                                        delete
                                                    </i>
                                                </IconButton>
                                            </Tooltip>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        )}
                    </Table>
                </TableContainer>
            </Paper>
        );
    };

    const renderAddEditBody = () => {
        if (!isEdit && !isAdd) {
            return <div />;
        } else {
            return (
                <div>
                    <TextField
                        id="Ident_Number"
                        label="Id"
                        type="text"
                        disabled={true}
                        value={selected.IdentNumber ? selected.IdentNumber : ""}
                        inputProps={{ maxLength: "256" }}
                        style={{ width: "100%" }}
                    />
                    <TextField
                        id="First_Name"
                        label="First Name"
                        type="text"
                        disabled={true}
                        value={donor && donor.FirstName ? donor.FirstName : ""}
                        inputProps={{ maxLength: "256" }}
                        style={{ width: "100%" }}
                    />
                    <TextField
                        id="Last_Name"
                        label="Surname/Last Name"
                        type="text"
                        disabled={true}
                        value={donor && donor.FirstName ? donor.LastName : ""}
                        inputProps={{ maxLength: "256" }}
                        style={{ width: "100%" }}
                    />
                    <TextField
                        id="Salutation"
                        label="Salutation"
                        type="text"
                        onChange={e => {
                            const updateSelected: Address = { ...selected };
                            updateSelected.Salutation = e.target.value as string;
                            setSelected(updateSelected);
                            onUpdate(updateSelected);
                        }}
                        value={selected.Salutation ? selected.Salutation : ""}
                        inputProps={{ maxLength: "35" }}
                        style={{ width: "100%" }}
                    />
                    <TextField
                        id="House_Number"
                        label="House Number"
                        type="text"
                        value={selected.HouseNumber ? selected.HouseNumber : ""}
                        onChange={e => {
                            const reg = new RegExp(/(^\d{0,10}$)/);
                            if (reg.test(e.target.value) || e.target.value == "") {
                                // ensure only numbers and empty strings save
                                const updateSelected: Address = { ...selected };
                                updateSelected.HouseNumber = e.target.value as string;
                                setSelected(updateSelected);
                                onUpdate(updateSelected);
                            }
                        }}
                        inputProps={{ maxLength: "10" }}
                        style={{ width: "100%" }}
                    />
                    <TextField
                        id="Street_Name"
                        label="Street"
                        type="text"
                        value={selected.Street ? selected.Street : ""}
                        onChange={e => {
                            const updateSelected: Address = { ...selected };
                            updateSelected.Street = e.target.value as string;
                            setSelected(updateSelected);
                            onUpdate(updateSelected);
                        }}
                        inputProps={{ maxLength: "35" }}
                        style={{ width: "100%" }}
                    />
                    <TextField
                        id="Address_2"
                        label="Address 2"
                        type="text"
                        value={selected.Address2 ? selected.Address2 : ""}
                        onChange={e => {
                            const updateSelected: Address = { ...selected };
                            updateSelected.Address2 = e.target.value as string;
                            setSelected(updateSelected);
                            onUpdate(updateSelected);
                        }}
                        inputProps={{ maxLength: "35" }}
                        style={{ width: "100%" }}
                    />
                    <TextField
                        id="Address_3"
                        label="Address 3"
                        type="text"
                        value={selected.Address3 ? selected.Address3 : ""}
                        onChange={e => {
                            const updateSelected: Address = { ...selected };
                            updateSelected.Address3 = e.target.value as string;
                            setSelected(updateSelected);
                            onUpdate(updateSelected);
                        }}
                        inputProps={{ maxLength: "35" }}
                        style={{ width: "100%" }}
                    />
                    <TextField
                        id="City_Name"
                        label="City"
                        type="text"
                        value={selected.City ? selected.City : ""}
                        onChange={e => {
                            const updateSelected: Address = { ...selected };
                            updateSelected.City = e.target.value as string;
                            setSelected(updateSelected);
                            onUpdate(updateSelected);
                        }}
                        inputProps={{ maxLength: "43" }}
                        style={{ width: "100%" }}
                    />
                    <FormControl style={{ width: "100%" }}>
                        <InputLabel htmlFor="StateAbbrev">State</InputLabel>
                        <Select
                            id="StateAbbrev"
                            onChange={e => {
                                const updateSelected: Address = { ...selected };
                                updateSelected.State = e.target.value as string;
                                setSelected(updateSelected);
                                onUpdate(updateSelected);
                            }}
                            value={selected.State ? selected.State : ""}
                        >
                            <MenuItem key="default-state" value="">
                                Select State...
                            </MenuItem>
                            {states.map(state => (
                                <MenuItem key={state.abbreviation} value={state.abbreviation}>
                                    {state.name}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    <TextField
                        id="Zip_Code"
                        label="Zip Code"
                        type="text"
                        value={selected.Zip ? selected.Zip : ""}
                        onChange={e => {
                            const reg = new RegExp(/(^\d{0,5}$)/);
                            if (reg.test(e.target.value) || e.target.value == "") {
                                // ensure only numbers and empty strings save
                                const updateSelected: Address = { ...selected };
                                updateSelected.Zip = e.target.value as string;
                                setSelected(updateSelected);
                                onUpdate(updateSelected);
                            }
                        }}
                        error={Boolean(selected.Zip && selected.Zip.length != 5)}
                        helperText={selected.Zip && selected.Zip.length != 5 && "Enter a 5 digit zip code."}
                        inputProps={{ maxLength: "5" }}
                        style={{ width: "100%" }}
                    />
                    <TextField
                        id="Zip_Plus_Four"
                        label="Zip + 4"
                        type="text"
                        value={selected.Zip4 ? selected.Zip4 : ""}
                        onChange={e => {
                            const reg = new RegExp(/(^\d{0,4}$)/);
                            if (reg.test(e.target.value) || e.target.value == "") {
                                // ensure only numbers and empty strings save
                                const updateSelected: Address = { ...selected };
                                updateSelected.Zip4 = e.target.value as string;
                                setSelected(updateSelected);
                                onUpdate(updateSelected);
                            }
                        }}
                        error={Boolean(selected.Zip4 && selected.Zip4.length != 4)}
                        helperText={selected.Zip4 && selected.Zip4.length != 4 && "Enter a 4 digit plus 4 code."}
                        inputProps={{ maxLength: "4" }}
                        style={{ width: "100%" }}
                    />
                    <TextField
                        id="County"
                        label="County"
                        type="text"
                        value={selected.County ? selected.County : ""}
                        onChange={e => {
                            const updateSelected: Address = { ...selected };
                            updateSelected.County = e.target.value as string;
                            setSelected(updateSelected);
                            onUpdate(updateSelected);
                        }}
                        inputProps={{ maxLength: "20" }}
                        style={{ width: "100%" }}
                    />
                    <TextField
                        id="Country"
                        label="Country"
                        type="text"
                        value={selected.Country ? selected.Country : ""}
                        onChange={e => {
                            const updateSelected: Address = { ...selected };
                            updateSelected.Country = e.target.value as string;
                            setSelected(updateSelected);
                            onUpdate(updateSelected);
                        }}
                        inputProps={{ maxLength: "20" }}
                        style={{ width: "100%" }}
                    />
                    <FormControl style={{ width: "100%" }}>
                        <InputLabel htmlFor={`AddressType`}>Address Type</InputLabel>
                        <Select
                            id={`AddressType`}
                            onChange={e => {
                                const updateSelected: Address = { ...selected };
                                updateSelected.AddressType = e.target.value as string;
                                setSelected(updateSelected);
                                onUpdate(updateSelected);
                            }}
                            value={selected.AddressType}
                        >
                            {addressTypes.map(option => (
                                <MenuItem key={option.Type} value={option.Type}>
                                    {option.Combined}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    <TextField
                        id="Telephone_Number"
                        label="Phone Number"
                        type="text"
                        value={selected.Telephone ? selected.Telephone : ""}
                        onChange={e => {
                            const reg = new RegExp(/(^\d{0,10}$)/);
                            if (reg.test(e.target.value) || e.target.value == "") {
                                // ensure only numbers and empty strings save
                                const updateSelected: Address = { ...selected };
                                updateSelected.Telephone = e.target.value as string;
                                setSelected(updateSelected);
                                onUpdate(updateSelected);
                            }
                        }}
                        error={Boolean(selected.Telephone && selected.Telephone.length != 10)}
                        helperText={
                            selected.Telephone && selected.Telephone.length != 10 && "Enter a 10 digit phone number."
                        }
                        inputProps={{ maxLength: "10" }}
                        style={{ width: "100%" }}
                    />
                    <TextField
                        id="Fax_Number"
                        label="Fax"
                        type="text"
                        value={selected.Fax ? selected.Fax : ""}
                        onChange={e => {
                            const reg = new RegExp(/(^\d{0,10}$)/);
                            if (reg.test(e.target.value) || e.target.value == "") {
                                // ensure only numbers and empty strings save
                                const updateSelected: Address = { ...selected };
                                updateSelected.Fax = e.target.value as string;
                                setSelected(updateSelected);
                                onUpdate(updateSelected);
                            }
                        }}
                        error={Boolean(selected.Fax && selected.Fax.length != 10)}
                        helperText={selected.Fax && selected.Fax.length != 10 && "Enter a 10 digit fax number."}
                        inputProps={{ maxLength: "10" }}
                        style={{ width: "100%" }}
                    />
                    <TextField
                        id="Internet_Address"
                        label="Email"
                        type="text"
                        error={!isEmailsValid}
                        defaultValue={selected.InternetAddress ? selected.InternetAddress : ""}
                        onChange={handleEmails}
                        inputProps={{ maxLength: "64" }}
                        style={{ width: "100%" }}
                        helperText={!isEmailsValid ? "Emails are invalid" : ""}
                    />
                    <TextField
                        id="Seasonal_Begin_Date"
                        label="Begin Date"
                        type="date"
                        value={
                            selected.SeasonalBeginDate != null
                                ? selected.SeasonalBeginDate
                                : formatDate(
                                      otherAddresses && otherAddresses[0].SeasonalBeginDate
                                          ? otherAddresses[0].SeasonalBeginDate
                                          : ""
                                  )
                        }
                        onChange={e => setEditValues(e, "SeasonalBeginDate")}
                        style={{ width: "100%" }}
                        InputLabelProps={{
                            shrink: true,
                        }}
                    />
                    <TextField
                        id="Seasonal_End_Date"
                        label="End Date"
                        type="date"
                        value={
                            selected.SeasonalEndDate != null
                                ? selected.SeasonalEndDate
                                : formatDate(
                                      otherAddresses && otherAddresses[0].SeasonalEndDate
                                          ? otherAddresses[0].SeasonalEndDate
                                          : ""
                                  )
                        }
                        onChange={e => setEditValues(e, "SeasonalEndDate")}
                        style={{ width: "100%" }}
                        InputLabelProps={{
                            shrink: true,
                        }}
                    />
                    <TextField
                        id="Notes"
                        label="Notes"
                        type="text"
                        value={selected.Notes ? selected.Notes : ""}
                        onChange={e => {
                            const updateSelected: Address = { ...selected };
                            updateSelected.Notes = e.target.value as string;
                            setSelected(updateSelected);
                            onUpdate(updateSelected);
                        }}
                        inputProps={{ maxLength: "500" }}
                        style={{ width: "100%" }}
                    />
                </div>
            );
        }
    };

    const renderDeleteBody = () => {
        if (!isDelete) {
            return <div />;
        } else {
            return (
                <div>
                    <p> Warning! This action cannot be undone. Are you sure you want to delete this address?</p>
                </div>
            );
        }
    };

    let body = <div />;
    if (isEdit || isAdd) {
        body = renderAddEditBody();
    } else if (isDelete) {
        body = renderDeleteBody();
    } else {
        body = renderResultTable();
    }

    return (
        <div>
            {body}
            {!isEdit && !isAdd && !isDelete ? (
                <button
                    className="material-button"
                    onClick={() => onAddClick()}
                    style={{ margin: "20px 10px", position: "relative", top: "-5px" }}
                >
                    <span>Add Other Address</span>
                </button>
            ) : null}
            {isEdit || isAdd || isDelete ? (
                <button
                    className="material-button"
                    onClick={() => onBackClick()}
                    style={{ margin: "20px 10px", position: "relative", top: "-5px" }}
                >
                    <span>Back</span>
                </button>
            ) : null}
        </div>
    );
};

export default OtherAddresses;
